import { WEEKDAY_SLUG, clockWindow } from '../calc/timefilter.js';
import { el } from './dom.js';
import { currentFilter } from './filters.js';
import { selectTab, setClock, syncTimeInputs } from './main.js';
import { state } from './state.js';

/* --------------------------------------------------------------------------
   Urvalet i adressen

   Period, veckodagar, klockslag, butiker, kassor, kassörer, kunder, artikelgrupper,
   leverantörer, jämförelse, utvecklingsvyns spann och flik sparas efter # i
   adressen. Kunden kan bokmärka en vy och öppna den igen med nästa månads
   export — ett bokmärke per veckodagskombination är också sättet att spara
   egna kombinationer, som "helger efter 16".

   Fragmentet efter # skickas aldrig till servern och följer inte med i
   Referer, så urvalet stannar i webbläsaren precis som kvittona. Värden som
   inte finns i den uppladdade filen hoppas över — en länk gjord med en
   export öppnas utan fel med en annan.

     #tid=manad&m=2026-03&dagar=fre,lor,son&kl=16:00-21:00&butik=1&butik=2
      &grupp=Försäljning||Adapter&lev=10&lev=11&flik=pMargin

   Ett enda värde per filter ("butik=1") är samma sak som förut, så adresser
   från innan flervalen fanns öppnas som de gjorde.
   -------------------------------------------------------------------------- */

const MODES = {today:'idag', date:'dag', month:'manad', range:'intervall'};
const MODE_IDS = {idag:'tToday', dag:'tDate', manad:'tMonth', intervall:'tRange'};

/** Sätts medan adressen läses in, så att inläsningen inte skriver tillbaka. */
let restoring = false;

/** Skriver det aktuella urvalet till adressen. Ersätter, lägger inte till i historiken. */
export function writeHash(){
  if(restoring || !state.bounds) return;
  const f = currentFilter();
  const p = new URLSearchParams();
  p.set('tid', MODES[f.mode]);
  if(f.mode === 'date'  && f.date)  p.set('d', f.date);
  if(f.mode === 'month' && f.month) p.set('m', f.month);
  if(f.mode === 'range'){
    if(f.from) p.set('fran', f.from);
    if(f.to)   p.set('till', f.to);
  }
  if(f.weekdays.length && f.weekdays.length < 7){
    p.set('dagar', [...f.weekdays].sort().map(d => WEEKDAY_SLUG[d]).join(','));
  }
  // Klockslagen skrivs bara när de faktiskt begränsar urvalet
  if(clockWindow(f.timeFrom, f.timeTo)) p.set('kl', `${f.timeFrom}-${f.timeTo}`);
  f.stores.forEach(v => p.append('butik', v));
  f.registers.forEach(v => p.append('kassa', v));
  f.cashiers.forEach(v => p.append('kassor', v));
  f.customers.forEach(v => p.append('kund', v));
  f.groups.forEach(g => p.append('grupp', g));
  f.suppliers.forEach(s => p.append('lev', s));
  if(state.compareMode !== 'prev') p.set('jmf', state.compareMode);
  if(state.trendSpan === 'period') p.set('spann', 'period');
  const tab = document.querySelector('.tab[aria-selected="true"]');
  if(tab && tab.dataset.panel !== 'pOverview') p.set('flik', tab.dataset.panel);

  // Komma och kolon behöver inte kodas i ett fragment: "dagar=fre,lor,son"
  // och "kl=16:00-21:00" går då att läsa och skriva för hand
  const hash = '#' + p.toString().replace(/%2C/gi, ',').replace(/%3A/gi, ':');
  if(window.location.hash === hash) return;
  try{
    history.replaceState(null, '', hash);
  }catch{
    // Vissa miljöer tillåter inte replaceState. Verktyget fungerar ändå.
  }
}

/**
 * Ställer in väljarna efter adressen. Anropas efter att filen lästs in och
 * väljarna fyllts, före första beräkningen. Ogiltiga värden hoppas över.
 * Returnerar fliken som ska visas, eller null.
 */
export function restoreFromHash(){
  let p;
  try{ p = new URLSearchParams(window.location.hash.slice(1)); }
  catch{ return null; }
  if(![...p.keys()].length) return null;

  restoring = true;
  try{
    const b = state.bounds;
    const inom = d => /^\d{4}-\d{2}-\d{2}$/.test(d || '') && d >= b.min && d <= b.max;

    const mode = MODE_IDS[p.get('tid')];
    if(mode) el(mode).checked = true;
    if(inom(p.get('d')))    el('fDate').value = p.get('d');
    if(b.months.includes(p.get('m'))) el('fMonth').value = p.get('m');
    // Veckodagar som inte går att tolka hoppas över, liksom ogiltiga klockslag
    const dagar = (p.get('dagar') || '').split(',');
    document.querySelectorAll('input.wd').forEach(c => { c.checked = dagar.includes(WEEKDAY_SLUG[c.value]); });
    const [kf = '', kt = ''] = (p.get('kl') || '').split('-');
    setClock('fClockFrom', kf);
    setClock('fClockTo', kt);
    if(inom(p.get('fran'))) el('fFrom').value = p.get('fran');
    if(inom(p.get('till'))) el('fTo').value = p.get('till');
    syncTimeInputs();

    // MultiSelect hoppar själv över värden som inte finns i listan
    state.scopeSelects.store.value    = p.getAll('butik');
    state.scopeSelects.register.value = p.getAll('kassa');
    state.scopeSelects.cashier.value  = p.getAll('kassor');
    state.scopeSelects.customer.value = p.getAll('kund');
    state.lineSelects.group.value    = p.getAll('grupp');
    state.lineSelects.supplier.value = p.getAll('lev');

    // Utan jmf i adressen gäller föregående period, precis som övriga
    // värden som saknas i adressen återställs
    const yoy = [...el('compareMode').options].find(o => o.value === 'yoy');
    state.compareMode = p.get('jmf') === 'yoy' && yoy && !yoy.disabled ? 'yoy' : 'prev';
    el('compareMode').value = el('compareModeAlt').value = state.compareMode;
    state.trendSpan = p.get('spann') === 'period' ? 'period' : 'file';
    el('trendSpan').value = state.trendSpan;
    const flik = p.get('flik');
    // En flik som inte finns för den här filen, till exempel Kunder, hoppas över
    return [...document.querySelectorAll('.tab')].find(tb => tb.dataset.panel === flik && !tb.hidden) || null;
  }finally{
    restoring = false;
  }
}

/** Kunden ändrar adressen för hand eller klistrar in en annan: följ med. */
export function watchHash(rerender){
  window.addEventListener('hashchange', () => {
    if(!state.bounds || restoring) return;
    const tab = restoreFromHash();
    rerender();
    if(tab) selectTab(tab);
  });
}
